import { getAllNumbersByMonthForCareerProgression } from './allNumbersHelpers';

function getRcDutyYearLimits(serviceMember, numberOfRcMonthsInServiceYear) {
  const reserveComponent = {};

  reserveComponent.rcDutyYearPartialYearLimit = serviceMember.RC_DUTY_YEAR_PARTIAL_YEAR_LIMITS.find((o) => o.monthsRemainingInYearOfService === numberOfRcMonthsInServiceYear);

  reserveComponent.activeDutyDays = reserveComponent.rcDutyYearPartialYearLimit.activeDutyDaysDefault;
  reserveComponent.membershipPoints = reserveComponent.rcDutyYearPartialYearLimit.membershipPointsDefault;
  reserveComponent.inactiveDutyDaysPaid = reserveComponent.rcDutyYearPartialYearLimit.inactiveDutyDaysPaidDefault;
  reserveComponent.inactiveDutyDaysNonPaid = reserveComponent.rcDutyYearPartialYearLimit.inactiveDutyDaysNonPaidDefault;
  reserveComponent.isValid = true;

  return reserveComponent;
}

/**
 * Get rank override list based on current rank and year of service.
 *
 * When rank = 'O-4' or 'O-5' or 'O-6', need to know if the service member's career includes
 * prior enlistment.
 */
export function getOverrideRanks(rank, yearOfService) {
  yearOfService = parseInt(yearOfService, 10);

  const ranks = {
    // Enlisted ranks
    'E-1': ['E-1', 'E-2', 'E-3'],
    'E-2': ['E-2', 'E-3', 'E-4'],
    'E-3': ['E-2', 'E-3', 'E-4', 'E-5', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-4': ['E-3', 'E-4', 'E-5', 'E-6', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-5': ['E-4', 'E-5', 'E-6', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-6': ['E-5', 'E-6', 'E-7', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-7': ['E-6', 'E-7', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-8': ['E-7', 'E-8', 'W-1', 'W-2', 'W-3', 'W-4'],
    'E-9': ['E-8', 'E-9', 'W-1', 'W-2', 'W-3', 'W-4'],

    // Warrant Officer ranks
    'W-1': ['W-1', 'W-2'],
    'W-2': ['W-1', 'W-2', 'W-3'],
    'W-3': ['W-2', 'W-3', 'W-4'],
    'W-4': ['W-3', 'W-4'],
    'W-5': ['W-4', 'W-5'],

    // Officer ranks
    'O-1': ['O-1', 'O-2'],
    'O-2': ['O-2', 'O-3'],
    'O-3': ['O-2', 'O-3', 'O-4'],
    'O-4': ['O-3', 'O-3E', 'O-4', 'O-5'],
    'O-5': ['O-4', 'O-5', 'O-6'],
    'O-6': ['O-5', 'O-6', 'O-7'],
    'O-7': ['O-6', 'O-7', 'O-8'],
    'O-8': ['O-7', 'O-8', 'O-9'],
    'O-9': ['O-7', 'O-8', 'O-9', 'O-10'],
    'O-10': ['O-10'],

    // Prior Enlisted ranks
    'O-1E': ['O-1E', 'O-2E'],
    'O-2E': ['O-1E', 'O-2E', 'O-3E'],
    'O-3E': ['O-2E', 'O-3E', 'O-4'],
  };

  const overrideRanks = ranks[rank] ? [...ranks[rank]] : [];

  //  Add ranks based on conditional years of service.
  if (rank === 'E-3' || rank === 'E-4' || rank === 'E-5' || rank === 'E-6' || rank === 'E-7' || rank === 'E-8' || rank === 'E-9') {
    if (rank === 'E-7' && yearOfService >= 8) overrideRanks.splice(overrideRanks.indexOf('W-1'), 0, 'E-8');
    if (rank === 'E-8' && yearOfService >= 10) overrideRanks.splice(overrideRanks.indexOf('W-1'), 0, 'E-9');
    if (yearOfService >= 20) overrideRanks.push('W-5');
    if (yearOfService >= 5) overrideRanks.push('O-1E');
  }

  //  Add ranks based on conditional years of service.
  // if (rank === 'W-1' || rank === 'W-2' || rank === 'W-3' || rank === 'W-4' || rank === 'W-5') {
  if (rank === 'W-1' || rank === 'W-2' || rank === 'W-3' || rank === 'W-5') {
    if (yearOfService >= 5) overrideRanks.push('O-1E');
  } else if (rank === 'W-4') {
    if (yearOfService >= 20) overrideRanks.push('W-5');
    if (yearOfService >= 5) overrideRanks.push('O-1E');
  }

  return overrideRanks;
}


//
//  For service member's who are 'under performing', adjust their career to 'catch up' ASAP.
//  More or less, the service member is given the benefit of the doubt and the system expects
//  they will get 'back on track'.
//
function adjustCareerProgressionForUnderPerformers(careerProgression, serviceMember) {
  const { currentRank } = serviceMember;
  const currentYearOfService = serviceMember.currentYearOfService();
  const officerSuggestedCareerProgression = serviceMember.globalSettings.careerProgression.officer;
  const enlistedSuggestedCareerProgression = serviceMember.globalSettings.careerProgression.enlisted;
  const warrantOfficerSuggestedCareerProgression = serviceMember.globalSettings.careerProgression.warrantOfficer;
  const priorEnlistedSuggestedCareerProgression = serviceMember.globalSettings.careerProgression.priorEnlisted;

  let careerProgressionIndex = -1;

  if (currentRank === 'E-2' && currentYearOfService > 2) careerProgressionIndex = 0; // enter grade progression at YOS 1
  else if (currentRank === 'E-3' && currentYearOfService > 4) careerProgressionIndex = 1; // enter grade progression at YOS 2
  else if (currentRank === 'E-4' && currentYearOfService > 6) careerProgressionIndex = 4; // enter grade progression at YOS 5
  else if (currentRank === 'O-1' && currentYearOfService > 2) careerProgressionIndex = 1; // enter grade progression at YOS 2
  else if (currentRank === 'O-2' && currentYearOfService > 4) careerProgressionIndex = 3; // enter grade progression at YOS 4
  else if (currentRank === 'O-3' && currentYearOfService > 11) careerProgressionIndex = 10; // enter grade progression at YOS 11
  else if (currentRank === 'W-1' && currentYearOfService > 4) careerProgressionIndex = 2; // enter grade progression at YOS 3
  else if (currentRank === 'O-1E') careerProgressionIndex = 0; // enter grade progression at YOS 1
  else if (currentRank === 'O-2E') careerProgressionIndex = 2; // enter grade progression at YOS 3
  else if (currentRank === 'O-3E') careerProgressionIndex = 4; // enter grade progression at YOS 5

  //  Only make rank adjustment if needed.
  if (careerProgressionIndex !== -1) {
    let adjustedRank = '';

    for (let index = 0; index <= careerProgression.length - 1; index += 1) {
      if (currentRank === 'E-2' && currentYearOfService > 2) adjustedRank = enlistedSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'E-3' && currentYearOfService > 4) adjustedRank = enlistedSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'E-4' && currentYearOfService > 6) adjustedRank = enlistedSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-1' && currentYearOfService > 2) adjustedRank = officerSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-2' && currentYearOfService > 4) adjustedRank = officerSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-3' && currentYearOfService > 11) adjustedRank = officerSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'W-1' && currentYearOfService > 4) adjustedRank = warrantOfficerSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-1E') adjustedRank = priorEnlistedSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-2E') adjustedRank = priorEnlistedSuggestedCareerProgression[careerProgressionIndex];
      else if (currentRank === 'O-3E') adjustedRank = priorEnlistedSuggestedCareerProgression[careerProgressionIndex];

      careerProgression[index].suggestedRank = adjustedRank;
      careerProgression[index].yearOfServiceRank = adjustedRank;
      careerProgression[index].overrideRanks = getOverrideRanks(adjustedRank, careerProgression[index].yearOfService);

      careerProgressionIndex += 1;
    }
  }

  return careerProgression;
}

/*
 *  Generate data to display upon initial entry into the Career Progressiin wizard page.
 */
export function getCareerService(serviceMember) {
  let careerService = [];

  const { currentRank } = serviceMember;
  let yearOfServiceRank;
  const rundate = serviceMember.rundate();
  const retSepDate = serviceMember.retSepDate();

  const allNumbersByMonth = getAllNumbersByMonthForCareerProgression(serviceMember);

  //  Determine current year of service and ending year of service.
  const yearOfServiceStart = allNumbersByMonth.find((o) => o.calendarYear === rundate.year() && o.calendarMonth === rundate.month() + 1).pebdServiceYear;
  const yearOfServiceEnd = allNumbersByMonth.find((o) => o.calendarYear === retSepDate.year() && o.calendarMonth === retSepDate.month() + 1).pebdServiceYear;

  //  Get the service member's performance level for the current service year (ie. behindTheCurve, ridingTheCurve, aheadOfTheCurve).
  //  How the service member is performing in their current service year will determine which ranks to display for each service year in Career Progression -
  //    1)  behindTheCurve ~ straight line the career progression from the current rank.
  //    2)  ridingTheCurve ~ display the government suggest career progression for each service year.
  //    3)  aheadOfTheCurve ~ straight line the career progression from the current rank.

  let yearOfServiceSuggestedRank = serviceMember.yearOfServiceSuggestedRank(yearOfServiceStart);
  const runDatePerformance = serviceMember.compareRanks(currentRank === 'E-1' ? yearOfServiceSuggestedRank : currentRank, yearOfServiceSuggestedRank);

  for (let serviceYear = yearOfServiceStart; serviceYear <= yearOfServiceEnd; serviceYear += 1) {
    switch (runDatePerformance) {
      case 'behindTheCurve':
      case 'aheadOfTheCurve':
        yearOfServiceSuggestedRank = currentRank;
        yearOfServiceRank = currentRank;
        break;
      case 'ridingTheCurve':
      default:
        yearOfServiceSuggestedRank = serviceMember.yearOfServiceSuggestedRank(serviceYear);
        yearOfServiceRank = yearOfServiceSuggestedRank;
        break;
    }

    //  Calculate number of total months (including partial months) and full months (in the future) remaining in service year.
    const monthsInServiceYear = allNumbersByMonth.filter((x) => x.pebdServiceYear === serviceYear);
    let numberOfMonthsRemainingInServiceYear = monthsInServiceYear.length;
    let numberOfFullMonthsRemainingInServiceYear = numberOfMonthsRemainingInServiceYear;
    if (serviceYear === serviceMember.currentYearOfService()) {
      numberOfMonthsRemainingInServiceYear = monthsInServiceYear.filter((x) => x.calendarYear >= rundate.year() && x.calendarMonth >= rundate.month() + 1).length;
      numberOfFullMonthsRemainingInServiceYear = numberOfMonthsRemainingInServiceYear - 1;
    }

    //  Retrieve career progression 'row' associated to service year.
    const obj = allNumbersByMonth.find((o) => o.pebdServiceYear === serviceYear);

    //  Persist info to career progression 'row'.
    const serviceYearData = {
      yearOfService: obj.pebdServiceYear,
      age: obj.ageYears,
      calendarYear: obj.calendarYear,
      defaultOverrideRank: yearOfServiceRank,
      suggestedRank: yearOfServiceSuggestedRank,
      overrideRanks: getOverrideRanks(yearOfServiceRank, obj.pebdServiceYear),
      yearOfServiceRank,
      numberOfMonthsRemainingInServiceYear,
      numberOfFullMonthsRemainingInServiceYear,
    };

    //  Get the count of RC months remaining in the service year.
    const countOfRcMonthsRemainingInServiceYear = serviceMember.countOfMonthsRemainingInServiceYear(allNumbersByMonth, 'RC', serviceYearData.yearOfService, rundate, retSepDate);

    if (countOfRcMonthsRemainingInServiceYear && countOfRcMonthsRemainingInServiceYear > 0) {
      serviceYearData.reserveComponent = getRcDutyYearLimits(serviceMember, countOfRcMonthsRemainingInServiceYear);
    }

    careerService.push(serviceYearData);
  }

  //  Adjust the career progression for certain ranks with a certain number years of service.
  careerService = adjustCareerProgressionForUnderPerformers(careerService, serviceMember);

  return careerService;
}
